// /menus — Admin Menu Management (PAGES.md §3.64)
//
// Manage menu categories, assign products, set display order, availability,
// and configure availability schedules (breakfast/lunch/dinner). "Locations" on a
// dish opens the master catalogue's Stores matrix in its menu form: turn the
// dish on at each location, mark it sold out at one (docs/master-catalogue-plan.md §17).
// RESTAURANT vertical only — MANAGER+ (level 30).
// Traceability: FR-ORD-001, FR-PROD-001

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Toggle,
  Badge,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Modal,
  DataTable,
  MenuCategoryNavigator,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { MenuCategory } from '@bbpos/ui/src/components/RestaurantComponents/MenuCategoryNavigator/types';
import { productService } from '@/services/product.service';
import { MasterStoresPanel } from '@/components/features/products/MasterStoresPanel';
import { categoryService } from '@/services/category.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { GLASS, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';

// ─── Icons ──────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function UtensilsIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2v0a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
    </svg>
  );
}

function OutletsIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
    </svg>
  );
}

// ─── Outlets of one dish ───────────────────────────────────────────────────

/** The Stores matrix (menu form) for the master of one outlet's dish row. */
function DishOutlets({ productId }: { productId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => productService.getById(productId),
  });
  if (isLoading) return <Skeleton variant="rect" width="100%" height={160} />;
  if (error || !data?.master_id) {
    return (
      <p className="py-6 text-center text-sm text-danger-600 dark:text-danger-400">
        Could not load the locations for this dish.
      </p>
    );
  }
  return <MasterStoresPanel masterId={data.master_id} menu />;
}

// ─── Food Type Dot ─────────────────────────────────────────────────────────

function FoodTypeDot({ type }: { type: string | null }) {
  if (type === 'veg') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm border border-emerald-600" aria-label="Vegetarian">
        <span className="w-2 h-2 rounded-full bg-emerald-600" />
      </span>
    );
  }
  if (type === 'non_veg') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm border border-red-600" aria-label="Non-vegetarian">
        <span className="w-2 h-2 rounded-full bg-red-600" />
      </span>
    );
  }
  if (type === 'egg') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm border border-amber-600" aria-label="Contains egg">
        <span className="w-2 h-2 rounded-full bg-amber-600" />
      </span>
    );
  }
  return null;
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProductRow {
  id: string;
  name: string;
  selling_price: number;
  is_active: boolean;
  display_order: number | null;
  category: string | null;
  food_type: string | null;
}

// ─── Constants ─────────────────────────────────────────────────────────────


// ─── Component ──────────────────────────────────────────────────────────────

function MenuManagementContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // ── Add category modal ──────────────────────────────────────────────────
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // ── Outlets modal (the dish at every outlet) ────────────────────────────
  const [outletsFor, setOutletsFor] = useState<ProductRow | null>(null);

  // ── Add item modal ──────────────────────────────────────────────────────
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [unassignedProducts, setUnassignedProducts] = useState<ProductRow[]>([]);
  const [itemSearch, setItemSearch] = useState('');
  const [isLoadingUnassigned, setIsLoadingUnassigned] = useState(false);

  // ── Fetch categories ────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      // Merge two sources: persisted categories (the categories table — so an
      // empty/just-created category like "Breakfast" still appears) and the
      // distinct categories actually used by products. Deduped by name.
      const [productCats, tableCats] = await Promise.all([
        productService.getCategories().catch(() => [] as string[]),
        categoryService.list({ is_active: true }).then((r) => r.data.map((c) => c.name)).catch(() => [] as string[]),
      ]);
      const names = [...new Set([...tableCats, ...productCats])].sort((a, b) => a.localeCompare(b));
      const menuCats: MenuCategory[] = names.map((c) => ({ id: c, name: c }));
      setCategories(menuCats);
      if (menuCats.length > 0 && !activeCategory) {
        setActiveCategory(menuCats[0].id);
      }
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load categories.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [toast, activeCategory]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // ── Fetch products for active category ──────────────────────────────────
  const fetchProducts = useCallback(async () => {
    if (!activeCategory) return;
    setIsLoadingProducts(true);
    try {
      const result = await productService.list({
        category: activeCategory,
        location_id: selectedLocationId ?? undefined,
        limit: 200,
      });
      setProducts(
        (result.data as unknown as ProductRow[]).sort(
          (a, b) => (a.display_order ?? 999) - (b.display_order ?? 999),
        ),
      );
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load products.', variant: 'error' });
    } finally {
      setIsLoadingProducts(false);
    }
  }, [activeCategory, selectedLocationId, toast]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // ── Toggle availability ─────────────────────────────────────────────────
  const handleToggleAvailability = useCallback(async (product: ProductRow) => {
    try {
      await productService.update(product.id, { is_active: !product.is_active } as Record<string, unknown>);
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, is_active: !p.is_active } : p)),
      );
      toast({ title: `${product.name} ${product.is_active ? 'disabled' : 'enabled'}`, variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to update.', variant: 'error' });
    }
  }, [toast]);

  // ── Reorder ─────────────────────────────────────────────────────────────
  const handleMoveUp = useCallback(async (index: number) => {
    if (index <= 0) return;
    const updated = [...products];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setProducts(updated);
    try {
      await Promise.all([
        productService.update(updated[index - 1].id, { display_order: index - 1 } as Record<string, unknown>),
        productService.update(updated[index].id, { display_order: index } as Record<string, unknown>),
      ]);
    } catch {
      toast({ title: 'Failed to reorder', variant: 'error' });
      fetchProducts();
    }
  }, [products, fetchProducts, toast]);

  const handleMoveDown = useCallback(async (index: number) => {
    if (index >= products.length - 1) return;
    const updated = [...products];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setProducts(updated);
    try {
      await Promise.all([
        productService.update(updated[index].id, { display_order: index } as Record<string, unknown>),
        productService.update(updated[index + 1].id, { display_order: index + 1 } as Record<string, unknown>),
      ]);
    } catch {
      toast({ title: 'Failed to reorder', variant: 'error' });
      fetchProducts();
    }
  }, [products, fetchProducts, toast]);

  // ── Add category ────────────────────────────────────────────────────────
  const handleAddCategory = useCallback(async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    if (categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: 'Category already exists', variant: 'error' });
      return;
    }
    try {
      // Persist to the categories table so it survives reload and shows even
      // before any product is assigned to it.
      await categoryService.create({ name: trimmed });
      setCategoryModalOpen(false);
      setNewCategoryName('');
      toast({ title: `Category "${trimmed}" added`, variant: 'success' });
      await fetchCategories();
      setActiveCategory(trimmed);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to add category', variant: 'error' });
    }
  }, [newCategoryName, categories, toast, fetchCategories]);

  // ── Add item to category ────────────────────────────────────────────────
  const openAddItemModal = useCallback(async () => {
    setItemSearch('');
    setAddItemModalOpen(true);
    setIsLoadingUnassigned(true);
    try {
      const result = await productService.list({
        location_id: selectedLocationId ?? undefined,
        limit: 200,
      });
      // Show products not already in this category
      const currentIds = new Set(products.map((p) => p.id));
      setUnassignedProducts(
        (result.data as unknown as ProductRow[]).filter((p) => !currentIds.has(p.id)),
      );
    } catch {
      toast({ title: 'Failed to load products', variant: 'error' });
    } finally {
      setIsLoadingUnassigned(false);
    }
  }, [selectedLocationId, products, toast]);

  const handleAddItem = useCallback(async (product: ProductRow) => {
    try {
      await productService.update(product.id, {
        category: activeCategory,
        display_order: products.length,
      } as Record<string, unknown>);
      setProducts((prev) => [...prev, { ...product, category: activeCategory, display_order: prev.length }]);
      setUnassignedProducts((prev) => prev.filter((p) => p.id !== product.id));
      toast({ title: `${product.name} added to ${activeCategory}`, variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to add item.', variant: 'error' });
    }
  }, [activeCategory, products.length, toast]);

  const filteredUnassigned = useMemo(() => {
    const q = itemSearch.toLowerCase();
    return unassignedProducts.filter((p) => p.name.toLowerCase().includes(q));
  }, [unassignedProducts, itemSearch]);

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns: ColumnDef<ProductRow>[] = useMemo(() => [
    {
      key: 'name', header: 'Item Name',
      render: (_v: unknown, row: ProductRow) => (
        <div className="flex items-center gap-2">
          <FoodTypeDot type={row.food_type} />
          <button
            type="button"
            onClick={() => navigate(`/products/${row.id}`)}
            className="font-bold text-neutral-900 dark:text-neutral-100 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-left"
          >
            {row.name}
          </button>
        </div>
      ),
    },
    {
      key: 'selling_price', header: 'Price', numeric: true,
      render: (_v: unknown, row: ProductRow) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatPaise(row.selling_price ?? 0, currency)}</span>
      ),
    },
    {
      key: 'is_active', header: 'Available',
      render: (_v: unknown, row: ProductRow) => (
        <Toggle
          checked={row.is_active}
          onChange={() => handleToggleAvailability(row)}
          aria-label={`Toggle availability for ${row.name}`}
        />
      ),
    },
    {
      key: 'locations', header: 'Locations',
      render: (_v: unknown, row: ProductRow) => (
        <Button variant="secondary" size="sm" onClick={() => setOutletsFor(row)}
          aria-label={`Locations for ${row.name}`}>
          <OutletsIcon /><span className="ml-1.5">Locations</span>
        </Button>
      ),
    },
    {
      key: 'order', header: 'Order',
      render: (_v: unknown, row: ProductRow, index: number) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => handleMoveUp(index)} disabled={index === 0}
            aria-label={`Move ${row.name} up`}>
            <ArrowUpIcon />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleMoveDown(index)} disabled={index === products.length - 1}
            aria-label={`Move ${row.name} down`}>
            <ArrowDownIcon />
          </Button>
        </div>
      ),
    },
  ], [handleToggleAvailability, handleMoveUp, handleMoveDown, products.length, navigate, currency]);

  return (
    <PageContainer
      title="Menu Management"
      subtitle={`${categories.length} categor${categories.length !== 1 ? 'ies' : 'y'}`}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Menus' }]}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/menus/periods')}>
            Dayparts
          </Button>
          {activeCategory && (
            <Button variant="secondary" size="sm" onClick={openAddItemModal}>
              <PlusIcon /><span className="ml-1.5">Add Item</span>
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={() => { setNewCategoryName(''); setCategoryModalOpen(true); }}>
            <PlusIcon /><span className="ml-1.5">Add Category</span>
          </Button>
        </div>
      }
    >
      <div className="animate-fade-in">
        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-4')}>
            <Skeleton variant="rect" width="100%" height={48} className="mb-4" />
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="30%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
                <Skeleton variant="rect" width={48} height={24} />
                <Skeleton variant="rect" width={64} height={24} />
              </div>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<UtensilsIcon />}
            title="No menu categories"
            description="Create your first category to organize your restaurant menu"
            action={{ label: 'Add Category', onClick: () => setCategoryModalOpen(true) }}
          />
        ) : (
          <>
            {/* Category Navigator */}
            <div className={cn(GLASS, 'p-4 mb-6')}>
              <MenuCategoryNavigator
                categories={categories}
                activeId={activeCategory}
                onChange={setActiveCategory}
              />
            </div>

            {/* Products Table */}
            <div className={cn(GLASS, 'overflow-hidden')}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100/50 dark:border-neutral-700/50">
                <div className="flex items-center gap-3">
                  <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    {activeCategory}
                  </h2>
                  <Badge variant="info" size="sm">
                    <span className="font-mono tabular-nums">{products.length}</span> items
                  </Badge>
                </div>
              </div>
              {isLoadingProducts ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <Skeleton variant="text" width="30%" height={18} />
                      <Skeleton variant="text" width="15%" height={18} />
                      <Skeleton variant="rect" width={48} height={24} />
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="p-8">
                  <EmptyState
                    variant="inline"
                    icon={<UtensilsIcon />}
                    title="No items in this category"
                    description="Add menu items to this category"
                    action={{ label: 'Add Item', onClick: openAddItemModal }}
                  />
                </div>
              ) : (
                <DataTable<ProductRow> columns={columns} data={products} />
              )}
            </div>
          </>
        )}
      </div>

      {/* Add Category Modal */}
      <Modal open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} title="Add Category">
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Category Name</label>
            <Input
              value={newCategoryName}
              onChange={(val) => setNewCategoryName(val)}
              placeholder="e.g. Starters, Main Course, Beverages"
              aria-label="Category name"
            />
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Need time-based menus (Breakfast / Lunch / Dinner)? Set them up under{' '}
            <button type="button" onClick={() => { setCategoryModalOpen(false); navigate('/menus/periods'); }} className="font-semibold text-brand-600 hover:underline">Dayparts</button>{' '}
            — assign items to time windows there.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setCategoryModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>Add Category</Button>
          </div>
        </div>
      </Modal>

      {/* Outlets Modal */}
      <Modal open={outletsFor !== null} onClose={() => setOutletsFor(null)}
        title={outletsFor ? `${outletsFor.name} at each location` : 'Locations'} variant="wide">
        <div className="p-1">
          {outletsFor && <DishOutlets productId={outletsFor.id} />}
        </div>
      </Modal>

      {/* Add Item Modal */}
      <Modal open={addItemModalOpen} onClose={() => setAddItemModalOpen(false)} title={`Add Item to ${activeCategory}`} variant="wide">
        <div className="space-y-4 p-1">
          <Input
            value={itemSearch}
            onChange={(val) => setItemSearch(val)}
            placeholder="Search products..."
            aria-label="Search products to add"
          />
          <div className="max-h-80 overflow-y-auto space-y-1">
            {isLoadingUnassigned ? (
              <div className="space-y-3 py-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} variant="rect" width="100%" height={48} />
                ))}
              </div>
            ) : filteredUnassigned.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">
                No products available to add
              </p>
            ) : (
              filteredUnassigned.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleAddItem(product)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <FoodTypeDot type={product.food_type} />
                    <div>
                      <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{product.name}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {product.category ?? 'Uncategorized'}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-mono tabular-nums text-neutral-600 dark:text-neutral-400">
                    {formatPaise(product.selling_price ?? 0, currency)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function MenuManagementPage() {
  return <ErrorBoundary><MenuManagementContent /></ErrorBoundary>;
}
